import { useCallback, useMemo, useState } from "react";

import {
  LinkedAccount,
  SetLinkedAccountResponse
} from "src/gen/schema/bilge/api";
import { User as Bot } from "src/gen/schema/models/user";

import { BindBotModal } from "../bindBotModal";
import { getDeleteMessageParts } from "../botUtils";
import { DeleteBindingModal } from "../deleteBindingModal";

type BindBotModalPayload = {
  readonly openBindBotModal: () => void;
  readonly bindBotModal: React.ReactElement | null;
};

export type BindSuccessCallback = (response: SetLinkedAccountResponse) => void;

/**
 * Hook to manage the Bind Bot modal state
 */
export const useBindBot = (
  handleSuccess: BindSuccessCallback,
  bots: Bot[]
): BindBotModalPayload => {
  const [open, setOpen] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const bindBotModal = useMemo(() => {
    if (open) {
      return (
        <BindBotModal
          bots={bots}
          show={open}
          onSuccess={handleSuccess}
          onClose={handleClose}
        />
      );
    }
    return null;
  }, [open, bots, handleClose, handleSuccess]);

  const openBindBotModal = useCallback((): void => {
    setOpen(true);
  }, []);

  return {
    openBindBotModal,
    bindBotModal
  };
};

type DeleteBotBindingPayload = {
  openDeleteBotBindingModal: (binding: LinkedAccount) => void;
  deleteBotBindingModal: React.ReactElement | null;
};

export type DeleteBindingSuccessCallback = () => void;

/**
 * Hook to manage the Delete Bot Binding modal state
 */
export const useDeleteBotBinding = (
  handleSuccess: DeleteBindingSuccessCallback
): DeleteBotBindingPayload => {
  const [binding, setBinding] = useState<LinkedAccount | undefined>();

  const handleClose = useCallback(() => {
    setBinding(undefined);
  }, []);

  const deleteBotBindingModal = useMemo(() => {
    if (binding) {
      const { platformName, channelName, serverName, channelLabel } =
        getDeleteMessageParts(binding);

      return (
        <DeleteBindingModal
          accountId={binding.accountId}
          botName={binding.userName}
          platformName={platformName}
          channelName={channelName}
          serverName={serverName}
          channelLabel={channelLabel}
          show={binding !== undefined}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      );
    }
    return null;
  }, [binding, handleClose, handleSuccess]);

  return {
    openDeleteBotBindingModal: setBinding,
    deleteBotBindingModal
  };
};
